import { Media, UUID } from "../../../infrastructure/ui/shared/types";
import { Price } from "../../projections/Price";
import type { SizeVariant } from "../sizeVariant/SizeVariant";
import { FashionModel } from "./FashionModel";
import { Feature } from "./Features";

enum ItemStatus {
  SELECTED = "SELECTED",
  DISCARDED = "DISCARDED",
  UNAVAILABLE = "UNAVAILABLE",
  RETURN_DONE = "RETURN_DONE",
  TO_RETURN = "TO_RETURN",
}

interface ProductProps {
  readonly available: boolean;
  readonly brand: string;
  readonly color: string;
  readonly fashionModel: FashionModel;
  readonly features: Feature[];
  readonly imageUrl: string;
  readonly itemId: string | undefined;
  readonly itemStatus: ItemStatus;
  readonly lastUnits: boolean;
  readonly lastUnitsStock: number | undefined;
  readonly media: Media[];
  readonly price: Price;
  readonly purchased: boolean;
  readonly reference: string;
  readonly size: string;
  readonly sizeVariants: SizeVariant[];
  readonly title: string;
  readonly variantId: UUID;
  readonly wishlisted: boolean;
}

class Product {
  available: boolean;
  brand: string;
  color: string;
  fashionModel: FashionModel;
  imageUrl: string;
  itemId: UUID | undefined;
  itemStatus: ItemStatus;
  lastUnits: boolean;
  lastUnitsStock: number | undefined;
  media?: Media[];
  price: Price;
  purchased: boolean;
  reference?: string;
  title: string;
  wishlisted: boolean;

  _features: Feature[];
  _size: string;
  _sizeVariants: SizeVariant[];
  _variantId: UUID;

  constructor({
    available,
    brand,
    color,
    fashionModel,
    features,
    imageUrl,
    itemId,
    itemStatus,
    lastUnits,
    lastUnitsStock,
    media,
    price,
    purchased,
    reference,
    size,
    sizeVariants,
    title,
    variantId,
    wishlisted,
  }: ProductProps) {
    this.available = available;
    this.brand = brand;
    this.color = color;
    this._features = features;
    this.fashionModel = fashionModel;
    this.imageUrl = imageUrl;
    this.itemId = itemId;
    this.itemStatus = itemStatus;
    this.lastUnits = lastUnits;
    this.lastUnitsStock = lastUnitsStock;
    this.media = media;
    this.price = price;
    this.purchased = purchased;
    this.reference = reference;
    this.wishlisted = wishlisted;
    this.title = title;
    this._size = size;
    this._sizeVariants = sizeVariants;
    this._variantId = variantId;
  }

  get features() {
    return this._features;
  }

  get hasAvailableSizes() {
    if (this.sizeVariants?.length) {
      return this.sizeVariants.some((variant) => variant.available);
    }

    return true;
  }

  get hasSizeVariants() {
    if (this.sizeVariants) {
      return this.sizeVariants.length > 0;
    }

    return false;
  }

  get isProductWithDiscount(): boolean {
    if (this.price?.discountedPrice) {
      return this.price.discountedPrice.amount !== this.price.amount;
    }
    return false;
  }

  get isAvailable() {
    if (this.sizeVariants) {
      const productSizeVariant = this.sizeVariants.find((sizeVariant) => sizeVariant.size === this.size);
      return productSizeVariant ? productSizeVariant.available : false;
    }

    return Boolean(this.available);
  }

  get size() {
    return this._size;
  }

  get sizeVariants() {
    return this._sizeVariants;
  }

  get variantId() {
    return this._variantId;
  }

  set features(value: Feature[]) {
    this._features = value;
  }

  set size(value: string) {
    this._size = value;
  }

  set sizeVariants(value: SizeVariant[]) {
    this._sizeVariants = value;
  }

  set variantId(value: UUID) {
    this._variantId = value;
  }

  static getImageAspectRatio(): number {
    return 4 / 5;
  }
}

export type { ProductProps };
export { Product, ItemStatus };
